import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import type { LabQuizDraft, LabQuizQuestion, LabQuizQuestionKind, LabQuizView } from '@/lib/types';

const KIND_LABELS: Record<LabQuizQuestionKind, string> = {
  short_text: 'Written answer',
  single_choice: 'One choice',
  multi_choice: 'Several choices',
};

function blankQuestion(): LabQuizQuestion {
  return { id: crypto.randomUUID(), kind: 'short_text', prompt: '', required: true };
}

const DESCRIPTION_WORDS = 1000;
const DESCRIPTION_CHARS = 12_000;

function wordCount(value: string): number {
  const text = value.trim();
  if (!text) return 0;
  return text.split(/\s+/).length;
}

function descriptionTooLong(value: string | null): boolean {
  const text = value ?? '';
  return text.length > DESCRIPTION_CHARS || wordCount(text) > DESCRIPTION_WORDS;
}

function blankDraft(): LabQuizDraft {
  return {
    description: null,
    questions: [blankQuestion()],
  };
}

function withKind(question: LabQuizQuestion, kind: LabQuizQuestionKind): LabQuizQuestion {
  if (kind === 'short_text') {
    return { id: question.id, kind, prompt: question.prompt, required: question.required };
  }
  const options = question.options && question.options.length >= 2
    ? question.options
    : [
      { id: crypto.randomUUID(), label: '' },
      { id: crypto.randomUUID(), label: '' },
    ];
  return { id: question.id, kind, prompt: question.prompt, required: question.required, options };
}

function withoutBlankOptions(draft: LabQuizDraft): LabQuizDraft {
  return {
    description: draft.description?.trim() || null,
    questions: draft.questions.map((question) => ({
      ...question,
      options: question.options?.filter((option) => option.label.trim()),
    })),
  };
}

function draftReady(title: string, draft: LabQuizDraft): boolean {
  if (!title.trim() || descriptionTooLong(draft.description)) return false;
  return draft.questions.every((question) => {
    if (!question.prompt.trim()) return false;
    if (question.kind === 'short_text') return true;
    return (question.options?.filter((option) => option.label.trim()).length ?? 0) >= 2;
  });
}

export function QuizEditor({
  projectId,
  quiz,
  onSaved,
  onDeleted,
  onCancel,
}: {
  projectId: string;
  quiz: LabQuizView | null;
  onSaved: (quiz: LabQuizView) => void;
  onDeleted: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(quiz?.title ?? '');
  const [draft, setDraft] = useState<LabQuizDraft>(quiz?.draft ?? blankDraft());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setTitle(quiz?.title ?? '');
    setDraft(quiz?.draft ?? blankDraft());
  }, [quiz]);

  const locked = quiz?.status === 'open';
  const ready = draftReady(title, draft);

  const updateQuestion = (id: string, next: LabQuizQuestion) => {
    setDraft((current) => ({
      ...current,
      questions: current.questions.map((question) => (question.id === id ? next : question)),
    }));
  };

  const save = async (nextTitle = title, nextDraft = draft) => {
    const cleaned = withoutBlankOptions(nextDraft);
    if (quiz) return labsApi.updateQuiz(quiz.id, { title: nextTitle.trim(), draft: cleaned });
    return labsApi.createQuiz(projectId, nextTitle.trim(), cleaned);
  };

  const run = async (action: () => Promise<LabQuizView | void>, success: string) => {
    setSaving(true);
    try {
      const result = await action();
      if (result) onSaved(result);
      else onDeleted();
      toast.success(success);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || locked) return;
        void run(() => save(), quiz ? 'Saved' : 'Quiz created');
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{quiz ? quiz.title : 'New quiz'}</h2>
        {quiz?.currentVersion && (
          <p className="text-sm text-muted-foreground">
            Version {quiz.currentVersion.version} · {quiz.currentVersion.responseCount} responses
          </p>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-[9rem_minmax(0,1fr)] sm:items-center">
        <Label htmlFor="quiz-title">Title</Label>
        <Input id="quiz-title" value={title} maxLength={120} disabled={locked || saving} onChange={(event) => setTitle(event.target.value)} />
      </div>

      <div className="space-y-2">
        <Field
          label="Description"
          id="quiz-description"
          value={draft.description ?? ''}
          disabled={locked || saving}
          onChange={(description) => setDraft((current) => ({ ...current, description: description || null }))}
        />
        {descriptionTooLong(draft.description) && (
          <p className="text-sm text-destructive sm:pl-[9.5rem]" role="alert">Description is too long</p>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex min-h-9 items-center justify-between gap-4">
          <h3 className="text-sm font-semibold">Questions</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={locked || saving || draft.questions.length >= 40}
            onClick={() => setDraft((current) => ({ ...current, questions: [...current.questions, blankQuestion()] }))}
          >
            <Plus />
            Add question
          </Button>
        </div>
        <ul className="divide-y rounded-lg border">
          {draft.questions.map((question, index) => (
            <li key={question.id} className="space-y-3 p-4">
              <div className="grid gap-3 sm:grid-cols-[11rem_minmax(0,1fr)_2.25rem] sm:items-center">
                <Select
                  value={question.kind}
                  onValueChange={(value) => updateQuestion(question.id, withKind(question, value as LabQuizQuestionKind))}
                  disabled={locked || saving}
                >
                  <SelectTrigger aria-label={`Question ${index + 1} type`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(KIND_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  aria-label={`Question ${index + 1}`}
                  value={question.prompt}
                  maxLength={500}
                  disabled={locked || saving}
                  onChange={(event) => updateQuestion(question.id, { ...question, prompt: event.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove question ${index + 1}`}
                  disabled={locked || saving || draft.questions.length === 1}
                  onClick={() => setDraft((current) => ({
                    ...current,
                    questions: current.questions.filter((item) => item.id !== question.id),
                  }))}
                >
                  <X />
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox
                  id={`required-${question.id}`}
                  checked={question.required}
                  disabled={locked || saving}
                  onCheckedChange={(value) => updateQuestion(question.id, { ...question, required: value === true })}
                />
                <Label htmlFor={`required-${question.id}`} className="font-normal">Required</Label>
              </div>
              {question.kind !== 'short_text' && (
                <div className="space-y-2 sm:pl-[11.75rem]">
                  {question.options?.map((option, optionIndex) => (
                    <div key={option.id} className="grid grid-cols-[minmax(0,1fr)_2.25rem] items-center gap-3">
                      <Input
                        aria-label={`Question ${index + 1} option ${optionIndex + 1}`}
                        value={option.label}
                        maxLength={160}
                        disabled={locked || saving}
                        onChange={(event) => updateQuestion(question.id, {
                          ...question,
                          options: question.options?.map((item) => (
                            item.id === option.id ? { ...item, label: event.target.value } : item
                          )),
                        })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Remove option ${optionIndex + 1}`}
                        disabled={locked || saving || (question.options?.length ?? 0) <= 2}
                        onClick={() => updateQuestion(question.id, {
                          ...question,
                          options: question.options?.filter((item) => item.id !== option.id),
                        })}
                      >
                        <X />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={locked || saving || (question.options?.length ?? 0) >= 8}
                    onClick={() => updateQuestion(question.id, {
                      ...question,
                      options: [...(question.options ?? []), { id: crypto.randomUUID(), label: '' }],
                    })}
                  >
                    <Plus />
                    Add option
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {quiz && quiz.totalResponses === 0 && (
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
          {quiz?.status === 'open' ? (
            <Button type="button" disabled={saving} onClick={() => void run(() => labsApi.closeQuiz(quiz.id), 'Quiz closed')}>
              Close quiz
            </Button>
          ) : (
            <>
              <Button type="submit" variant="outline" disabled={!ready || saving}>Save</Button>
              <Button
                type="button"
                disabled={!ready || saving}
                onClick={() => {
                  setSaving(true);
                  save()
                    .then(async (saved) => {
                      onSaved(saved);
                      const opened = await labsApi.openQuiz(saved.id);
                      onSaved(opened);
                      toast.success('Quiz opened');
                    })
                    .catch((err) => toast.error(getErrorMessage(err)))
                    .finally(() => setSaving(false));
                }}
              >
                Open quiz
              </Button>
            </>
          )}
        </div>
      </div>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this quiz?</AlertDialogTitle>
            <AlertDialogDescription>This quiz has no responses.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving || !quiz}
              onClick={() => {
                if (!quiz) return;
                setSaving(true);
                labsApi.deleteQuiz(quiz.id)
                  .then(() => {
                    onDeleted();
                    toast.success('Quiz deleted');
                  })
                  .catch((err) => toast.error(getErrorMessage(err)))
                  .finally(() => setSaving(false));
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

function Field({
  id,
  label,
  value,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-[9rem_minmax(0,1fr)]">
      <Label htmlFor={id} className="sm:pt-2">{label}</Label>
      <Textarea id={id} rows={4} maxLength={DESCRIPTION_CHARS} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}
